import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { api } from '../../services/api';
import Dialog from '../ui/Dialog';
import { apiErrorMessage } from '../sales/shared';
import {
  archiveItems,
  canArchive,
  nameMatches,
  nextStep,
  plainDeleteAvailable,
  purgeState,
  type ArchiveStep,
} from '../../utils/engagementArchiveModel';
import { deleteEngagementConfirmed } from '../../utils/engagementDeleteModel';

// ============================================================================
// D-265 sec.2-3 — the archive window. Opens at once and loads what goes with
// the engagement (tasks, calls, milestones, timers, hours). Open timers: a
// warning that they will stop. An OTE manager also gets "מחיקה לצמיתות":
// blocked (with the reason) once a charge was produced; otherwise type the
// engagement's name, then "בטוח?". Messages appear only after the server answers.
// QX-1273: the ONE "ארכיון / מחיקה" entry of the row menu opens it. Nothing under
// the engagement -> the regular delete (sec.1) instead of the permanent one; an
// engagement already in the archive -> no archive button.
// ============================================================================

export interface EngagementArchiveDialogProps {
  open: boolean;
  engagementId: string;
  name: string;
  onClose: () => void;
  /** After the server confirmed: 'archived', 'purged' or 'deleted' (the regular delete). */
  onDone: (kind: 'archived' | 'purged' | 'deleted', info: { canRestore: boolean }) => void;
  onError: (message: string) => void;
}

export default function EngagementArchiveDialog({ open, engagementId, name, onClose, onDone, onError }: EngagementArchiveDialogProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<ArchiveStep>('overview');
  const [typed, setTyped] = useState('');

  useEffect(() => {
    if (open) { setStep('overview'); setTyped(''); }
  }, [open, engagementId]);

  const preview = useQuery({
    queryKey: ['engagement-archive-preview', engagementId],
    queryFn: () => api.getEngagementArchivePreview(engagementId),
    enabled: open && !!engagementId,
    staleTime: 0,
  });
  const p = preview.data?.data;
  const plain = p ? plainDeleteAvailable(p) : false;
  const purge = p && !plain ? purgeState(p) : 'hidden';
  const showArchive = !p || canArchive(p);
  const displayName = p?.name ?? name;
  const info = { canRestore: p?.can_restore === true };

  const archive = useMutation({
    mutationFn: () => api.archiveEngagement(engagementId),
    onSuccess: () => onDone('archived', info),
    onError: (err: unknown) => onError(apiErrorMessage(err, t('engagements.errors.archive'))),
  });
  const purgeMutation = useMutation({
    mutationFn: () => api.purgeEngagement(engagementId, typed.trim()),
    onSuccess: () => onDone('purged', info),
    onError: (err: unknown) => onError(apiErrorMessage(err, t('engagements.archiveDialog.purgeFailed'))),
  });
  // D-265 sec.1 - the regular delete: "deleted" only after the server answered.
  const [deleting, setDeleting] = useState(false);
  const plainDelete = async () => {
    setDeleting(true);
    await deleteEngagementConfirmed(() => api.deleteEngagement(engagementId, true), {
      onSuccess: () => onDone('deleted', info),
      onError: (err: unknown) => onError(apiErrorMessage(err, t('engagements.errors.delete'))),
    });
    setDeleting(false);
  };
  const busy = archive.isPending || purgeMutation.isPending || deleting;

  let footer: React.ReactNode;
  if (step === 'overview') {
    footer = (
      <div className="flex flex-wrap gap-2 justify-end w-full">
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>{t('common.cancel')}</button>
        {plain && (
          <button type="button" className="btn btn-danger" disabled={busy} aria-busy={deleting || undefined} onClick={() => void plainDelete()} data-testid="plain-delete">
            {deleting ? t('engagements.archiveDialog.deleting') : t('engagements.deleteAction')}
          </button>
        )}
        {purge === 'available' && (
          <button type="button" className="btn btn-danger" disabled={busy || !p} onClick={() => setStep(nextStep(step, { typed, name: displayName, purge }))}>
            {t('engagements.archiveDialog.purge')}
          </button>
        )}
        {showArchive && (
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !p}
            aria-busy={archive.isPending || undefined}
            onClick={() => archive.mutate()}
          >
            {archive.isPending ? t('engagements.archiveDialog.archiving') : t('engagements.archiveAction')}
          </button>
        )}
      </div>
    );
  } else if (step === 'purge_name') {
    footer = (
      <div className="flex flex-wrap gap-2 justify-end w-full">
        <button type="button" className="btn btn-secondary" onClick={() => setStep('overview')} disabled={busy}>{t('common.back')}</button>
        <button
          type="button"
          className="btn btn-danger"
          disabled={!nameMatches(typed, displayName)}
          onClick={() => setStep(nextStep(step, { typed, name: displayName, purge }))}
        >
          {t('engagements.archiveDialog.continue')}
        </button>
      </div>
    );
  } else {
    footer = (
      <div className="flex flex-wrap gap-2 justify-end w-full">
        <button type="button" className="btn btn-secondary" onClick={() => setStep('overview')} disabled={busy}>{t('common.cancel')}</button>
        <button
          type="button"
          className="btn btn-danger"
          disabled={busy}
          aria-busy={purgeMutation.isPending || undefined}
          onClick={() => purgeMutation.mutate()}
        >
          {purgeMutation.isPending ? t('engagements.archiveDialog.purging') : t('engagements.archiveDialog.purgeSure')}
        </button>
      </div>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={() => { if (!busy) onClose(); }}
      dismissible={!busy}
      size="md"
      title={step === 'overview'
        ? t('engagements.archiveTitleNamed', { name: displayName })
        : t('engagements.deleteTitleNamed', { name: displayName })}
      footer={footer}
    >
      {preview.isLoading && <p className="text-text-2" data-testid="archive-loading">{t('engagements.archiveDialog.loading')}</p>}
      {preview.isError && <p className="text-danger" role="alert">{apiErrorMessage(preview.error, t('engagements.archiveDialog.loadFailed'))}</p>}
      {p && step === 'overview' && (
        <div className="space-y-3">
          <p>{t(showArchive ? 'engagements.archiveDialog.intro' : 'engagements.archiveDialog.alreadyArchived')}</p>
          {archiveItems(p).length === 0 ? (
            <p className="text-text-2">{t('engagements.archiveDialog.nothingUnder')}</p>
          ) : (
            <ul className="list-disc ps-5 space-y-1" data-testid="archive-items">
              {archiveItems(p).map((i) => (
                <li key={i.key}>
                  {t(`engagements.archiveDialog.items.${i.key}`, { count: i.count })}
                  {i.key === 'work_logs' ? ` (${t('engagements.archiveDialog.hours', { hours: p.counts.hours })})` : ''}
                  {i.open ? ` (${t('engagements.archiveDialog.openOf', { count: i.open })})` : ''}
                </li>
              ))}
            </ul>
          )}
          {p.open_timers.length > 0 && (
            <div className="rounded-md border border-warning p-3 space-y-1" role="alert">
              <p className="font-medium flex items-center gap-2">
                <AlertTriangle size={16} aria-hidden="true" /> {t('engagements.archiveDialog.timersWarning', { count: p.open_timers.length })}
              </p>
              <ul className="list-disc ps-5 text-sm">
                {p.open_timers.map((tm, idx) => (
                  <li key={idx}><bdi>{tm.user_name ?? ''}</bdi> · <bdi>{tm.ticket_title ?? ''}</bdi></li>
                ))}
              </ul>
            </div>
          )}
          {showArchive && <p className="text-sm text-text-2">{t('engagements.archiveDialog.billingNote')}</p>}
          {showArchive && <p className="text-sm text-text-2">{t(p.can_restore ? 'engagements.archiveDialog.restoreNoteManager' : 'engagements.archiveDialog.restoreNote')}</p>}
          {plain && <p className="text-sm text-text-2" data-testid="plain-delete-note">{t('engagements.archiveDialog.plainDeleteNote')}</p>}
          {purge === 'blocked_billed' && (
            <p className="text-sm rounded-md border border-border p-3" data-testid="purge-blocked">
              {t('engagements.archiveDialog.purgeBlocked')}
            </p>
          )}
        </div>
      )}
      {p && step === 'purge_name' && (
        <div className="space-y-3">
          <p className="text-danger">{t('engagements.archiveDialog.purgeWarning')}</p>
          <label htmlFor="eng-purge-name" className="block">{t('engagements.archiveDialog.typeName', { name: displayName })}</label>
          <input
            id="eng-purge-name"
            className="input w-full"
            value={typed}
            autoComplete="off"
            onChange={(e) => setTyped(e.target.value)}
          />
        </div>
      )}
      {p && step === 'purge_confirm' && (
        <p className="text-danger font-medium">{t('engagements.archiveDialog.areYouSure', { name: displayName })}</p>
      )}
    </Dialog>
  );
}
